import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchRinascenteInvoice,
  fetchRinascenteInvoicePdf,
  fetchRinascenteEmail,
  approveRinascenteInvoice,
  rejectRinascenteInvoice,
  reprocessRinascenteInvoice,
  lockRinascenteInvoice,
  unlockRinascenteInvoice,
  unlockRinascenteInvoiceBeacon,
} from "../../api/client";
import { StatusBadge } from "../../lib/status";
import { DetailPageShell } from "../../lib/DetailPageShell";
import type { RinascenteInvoice, RinascenteInvoiceData, RinascenteEmail, RinascenteStatus, RinascenteTipoDocumento } from "../../api/types";
import { EditableField } from "../../components/EditableField";

const LOCK_HB_MS = 2 * 60 * 1000; // heartbeat 2 minuti (TTL lock 5 min)
const CRITICAL_FIELDS_COUNT = 9; // matches _CRITICAL_FIELDS in src/rinascente/models.py

// Stato reso via <StatusBadge> condiviso (lib/status)

const TIPO_OPTIONS: { value: RinascenteTipoDocumento; label: string }[] = [
  { value: "unknown",    label: "Sconosciuto" },
  { value: "fattura",    label: "Fattura" },
  { value: "credit_memo", label: "Credit memo" },
];

function fmt(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtDateOnly(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

function completenessColor(v: number) {
  if (v >= 0.80) return "var(--indaco-success)";
  if (v >= 0.60) return "var(--indaco-warning)";
  return "var(--indaco-danger)";
}

/** Legge un path dotted dall'oggetto dati estratti (es. "documento.numero"). */
function getPath(obj: unknown, dotted: string): unknown {
  let cur: unknown = obj;
  for (const part of dotted.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

// ── PDF preview (object URL + iframe) ────────────────────────────────────────

function PdfPreview({ practiceId, filename, pageCount }: { practiceId: string; filename?: string | null; pageCount?: number | null }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let revoked = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    setCurrentPage(1);
    fetchRinascenteInvoicePdf(practiceId)
      .then((blob) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento PDF"))
      .finally(() => setLoading(false));

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [practiceId]);

  const pages = pageCount && pageCount > 0 ? pageCount : null;
  const canPrev = currentPage > 1;
  const canNext = pages ? currentPage < pages : false;
  const iframeSrc = blobUrl
    ? `${blobUrl}#view=FitH&toolbar=1&page=${currentPage}`
    : null;

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "var(--space-2)",
        padding: "var(--space-2) var(--space-3)",
        borderBottom: "1px solid var(--border-color)", background: "var(--indaco-gray-50)",
      }}>
        <button className="btn btn-outline" onClick={() => setCollapsed(!collapsed)}
          style={{ padding: "2px 8px", minWidth: 0 }} title={collapsed ? "Espandi" : "Comprimi"}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {collapsed ? "expand_more" : "expand_less"}
          </span>
        </button>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-danger)" }}>picture_as_pdf</span>
        <span className="fw-semibold text-sm truncate" style={{ flex: 1 }}>
          {filename || "PDF originale"}
        </span>
        {pages && (
          <span className="badge badge-neutral" title="Numero pagine del PDF">
            {pages} {pages === 1 ? "pagina" : "pagine"}
          </span>
        )}
      </div>

      {/* Toolbar navigazione + azioni */}
      {blobUrl && !error && !collapsed && (
        <div style={{
          display: "flex", alignItems: "center", gap: "var(--space-2)",
          padding: "var(--space-1) var(--space-3)",
          borderBottom: "1px solid var(--border-color)",
          background: "var(--indaco-gray-50)",
        }}>
          {/* Prev / counter / Next */}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-1)" }}>
            <button
              className="btn btn-outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={!canPrev}
              style={{ padding: "2px 8px", minWidth: 0 }}
              title="Pagina precedente"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_left</span>
            </button>
            <span className="text-sm" style={{ minWidth: 90, textAlign: "center" }}>
              {pages ? `Pagina ${currentPage} di ${pages}` : "Pagina 1"}
            </span>
            <button
              className="btn btn-outline"
              onClick={() => setCurrentPage((p) => Math.min(pages ?? p, p + 1))}
              disabled={!canNext}
              style={{ padding: "2px 8px", minWidth: 0 }}
              title="Pagina successiva"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
            </button>
          </div>

          <div style={{ flex: 1 }} />

          {/* Apri in tab (viewer nativo browser) */}
          {blobUrl && (
            <a
              className="btn btn-outline"
              href={`${blobUrl}#view=FitH`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ padding: "2px 10px", textDecoration: "none" }}
              title="Apri il PDF nel viewer nativo del browser (zoom, ricerca, tutte le pagine)"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: "middle" }}>open_in_new</span>
              <span style={{ verticalAlign: "middle", marginLeft: 4 }}>Apri</span>
            </a>
          )}

          {/* Download */}
          {blobUrl && (
            <a
              className="btn btn-primary"
              href={blobUrl}
              download={filename || `rinascente_${practiceId}.pdf`}
              style={{ padding: "2px 10px", textDecoration: "none" }}
              title="Scarica il PDF originale"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: "middle" }}>download</span>
              <span style={{ verticalAlign: "middle", marginLeft: 4 }}>Scarica</span>
            </a>
          )}
        </div>
      )}

      {!collapsed && (
        <div style={{ flex: 1, minHeight: 600, position: "relative", background: "var(--indaco-gray-100)" }}>
          {loading && (
            <div className="loading-center"><div className="spinner spinner-lg" /></div>
          )}
          {error && (
            <div style={{ padding: "var(--space-4)", textAlign: "center", color: "var(--indaco-danger)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 40 }}>error</span>
              <div className="text-sm">{error}</div>
              <div className="text-muted text-xs" style={{ marginTop: 4 }}>
                Il PDF originale potrebbe non essere disponibile sul filesystem.
              </div>
            </div>
          )}
          {iframeSrc && !error && (
            <iframe key={iframeSrc} src={iframeSrc} title={`Anteprima PDF fattura — pagina ${currentPage}`}
              style={{ width: "100%", height: "100%", minHeight: 600, border: "none" }} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Email originale (nascosta se 404/null nel POC) ───────────────────────────

function EmailPanel({ invoiceId }: { invoiceId: string }) {
  const [email, setEmail] = useState<RinascenteEmail | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    fetchRinascenteEmail(invoiceId)
      .then((e) => { if (mounted) setEmail(e); })
      .catch(() => { if (mounted) setEmail(null); });
    return () => { mounted = false; };
  }, [invoiceId]);

  // null/undefined → sezione nascosta (lo spazio serve per il PDF).
  if (!email) return null;

  return (
    <div className="card" style={{ padding: "var(--space-3)" }}>
      <h3 style={{ margin: "0 0 var(--space-2)", fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>mail</span>
        Email originale
      </h3>
      <div style={{ marginBottom: "var(--space-2)" }}>
        <div className="text-muted text-xs">Oggetto</div>
        <div className="fw-semibold text-sm">{email.subject || "—"}</div>
      </div>
      <div style={{ marginBottom: "var(--space-2)" }}>
        <div className="text-muted text-xs">Mittente</div>
        <div className="text-sm">{email.sender || "—"}</div>
      </div>
      <div style={{ marginBottom: "var(--space-2)" }}>
        <div className="text-muted text-xs">Ricevuta</div>
        <div className="text-sm">{fmt(email.received_at)}</div>
      </div>
      {email.body_text && (
        <div>
          <div className="text-muted text-xs">Corpo</div>
          <div style={{
            marginTop: "var(--space-1)", maxHeight: 260, overflowY: "auto",
            padding: "var(--space-2)", background: "var(--indaco-gray-50)",
            borderRadius: "var(--border-radius-sm)", border: "1px solid var(--border-color)",
            whiteSpace: "pre-wrap", fontSize: "var(--font-size-sm)", lineHeight: 1.5,
          }}>
            {email.body_text}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Componente principale ────────────────────────────────────────────────────

export function RinascenteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<RinascenteInvoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<Record<string, unknown>>({});
  const [lockOwner, setLockOwner] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const lockRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const inv = await fetchRinascenteInvoice(id);
      setInvoice(inv);
      setCorrections(inv.corrections ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore caricamento");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /* ── Presa in carico automatica (race-style come DDT) ── */
  const canBeLocked = invoice?.status === "pending_review";
  useEffect(() => {
    if (!id || !canBeLocked) return;
    let mounted = true;
    async function acquire() {
      try {
        const { status, data } = await lockRinascenteInvoice(id!);
        if (!mounted) return;
        if (status === 423) {
          setLockOwner(data?.locked_by_name || data?.locked_by || "altro operatore");
          setIsReadOnly(true);
        } else {
          setLockOwner(null);
          setIsReadOnly(false);
        }
      } catch { /* ignore */ }
    }
    acquire();
    lockRef.current = setInterval(acquire, LOCK_HB_MS);
    const onUnload = () => { try { unlockRinascenteInvoiceBeacon(id!); } catch { /* */ } };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      mounted = false;
      clearInterval(lockRef.current);
      window.removeEventListener("beforeunload", onUnload);
      unlockRinascenteInvoice(id!).catch(() => { });
    };
  }, [id, canBeLocked]);

  // Read-only anche se pratica già conclusa (approved/rejected) o in errore.
  const isConcluded = invoice?.status === "approved" || invoice?.status === "rejected";
  const readOnly = isReadOnly || isConcluded || (invoice?.status !== "pending_review");

  /** Valore effettivo di un campo = correction ?? extracted. */
  function effectiveValue(path: string): string | number | null {
    const v = path in corrections ? corrections[path] : getPath(invoice?.extracted_data, path);
    if (v === undefined || v === null) return null;
    return v as string | number;
  }

  /** Valore estratto originale (ignora corrections) — per highlight "modificato" + undo. */
  function originalValue(path: string): string | number | null {
    const v = getPath(invoice?.extracted_data, path);
    if (v === undefined || v === null) return null;
    return v as string | number;
  }

  function handleCorrect(path: string, value: string) {
    setCorrections(prev => {
      const next = { ...prev };
      if (value === "") delete next[path];
      else next[path] = value;
      return next;
    });
  }

  /** Annulla la singola modifica — ripristina il valore estratto. */
  function resetField(path: string) {
    setCorrections(prev => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }

  /** Azzera tutte le corrections. */
  function resetAllCorrections() {
    setCorrections({});
  }

  // Solo le corrections effettivamente diverse dall'estratto.
  const effectiveCorrections: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(corrections)) {
    const original = getPath(invoice?.extracted_data, k);
    if (original !== v) effectiveCorrections[k] = v;
  }
  const correctionsCount = Object.keys(effectiveCorrections).length;

  async function handleApprove() {
    if (!id) return;
    setIsProcessing(true);
    try {
      const res = await approveRinascenteInvoice(id, effectiveCorrections, approveNote);
      showToast(`Fattura approvata. Output: ${res.output_path}`, true);
      setShowApproveModal(false);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore approvazione", false);
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleReject() {
    if (!id) return;
    setIsProcessing(true);
    try {
      await rejectRinascenteInvoice(id, [], rejectNote);
      showToast("Fattura rifiutata", true);
      setShowRejectModal(false);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore rifiuto", false);
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleReprocess() {
    if (!id) return;
    setIsProcessing(true);
    try {
      const res = await reprocessRinascenteInvoice(id);
      showToast(`Rielaborata. ${res.numero ?? "—"}, ${res.n_righe} righe, completezza ${Math.round(res.completeness * 100)}%.`, true);
      setShowReprocessModal(false);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore reprocessing", false);
    } finally {
      setIsProcessing(false);
    }
  }

  if (isLoading) {
    return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  }

  if (error) {
    return (
      <div style={{ padding: "var(--space-6)" }}>
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span> {error}
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  const data: RinascenteInvoiceData | null = invoice.extracted_data;
  const completeness = invoice.completeness ?? 0;

  return (
    <>
      <DetailPageShell
        backTo="/rinascente"
        backLabel="Lista fatture"
        title={`Fattura ${data?.documento?.numero || invoice.source_ref || invoice.id.slice(0, 8)}`}
        titleId={invoice.id}
        badges={
          <>
            <StatusBadge status={invoice.status} />
            {invoice.is_credit_memo && (
              <span className="badge badge-warning" title="Credit memo / nota di credito">Credit memo</span>
            )}
          </>
        }
        meta={
          <span>
            Completezza <strong style={{ color: completenessColor(completeness) }}>{Math.round(completeness * 100)}%</strong>
            {" · "}{CRITICAL_FIELDS_COUNT} campi critici
            {completeness < 0.60 && <span style={{ color: "var(--indaco-danger)", marginLeft: "var(--space-2)" }}>· verifica prima di approvare</span>}
          </span>
        }
        approve={!readOnly ? { onClick: () => setShowApproveModal(true), disabled: isProcessing } : undefined}
        reprocess={!readOnly ? { onClick: () => setShowReprocessModal(true), disabled: isProcessing } : undefined}
        reject={!readOnly ? { onClick: () => setShowRejectModal(true), disabled: isProcessing } : undefined}
        readOnlyMessage={readOnly ? (isConcluded ? "Conclusa (sola lettura)" : lockOwner ? `In lavorazione da ${lockOwner}` : "Sola lettura") : undefined}
      >

      {/* Banner lock (sola lettura) */}
      {lockOwner && (
        <div className="card" style={{
          marginBottom: "var(--space-3)", padding: "var(--space-3)",
          background: "var(--indaco-warning-light)", borderLeft: "4px solid var(--indaco-warning)",
          color: "var(--indaco-gray-800)", display: "flex", alignItems: "center", gap: "var(--space-2)",
        }}>
          <span className="material-symbols-outlined" style={{ color: "var(--indaco-warning)" }}>lock</span>
          <span className="text-sm">In lavorazione da <strong>{lockOwner}</strong> — sola lettura.</span>
        </div>
      )}

      {/* Errore pipeline */}
      {invoice.error_message && (
        <div className="card" style={{
          marginBottom: "var(--space-3)", padding: "var(--space-3)",
          background: "var(--indaco-danger-light)", borderLeft: "4px solid var(--indaco-danger)",
          color: "var(--indaco-danger)", fontSize: "var(--font-size-sm)",
        }}>
          ⚠ {invoice.error_message}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.ok ? "toast-success" : "toast-error"}`} style={{ position: "fixed", top: "var(--space-4)", right: "var(--space-4)", zIndex: 9999 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{toast.ok ? "check_circle" : "error"}</span>
          <span className="text-sm">{toast.msg}</span>
        </div>
      )}

        {/* Correzioni in sospeso */}
        {!readOnly && correctionsCount > 0 && (
          <div className="badge badge-warning" style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span>{correctionsCount} correzion{correctionsCount === 1 ? "e" : "i"} da applicare</span>
            <button className="btn btn-ghost btn-sm" onClick={resetAllCorrections} disabled={isProcessing}
              title="Annulla tutte le modifiche e ripristina i valori estratti"
              style={{ padding: "2px 8px", fontSize: "var(--font-size-xs)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>restart_alt</span> Azzera
            </button>
          </div>
        )}

        {/* Body scrollabile (scroll interno; PDF sticky nella colonna sinistra) */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {/* Layout 2 colonne */}
          <div style={{ display: "grid", gridTemplateColumns: "460px 1fr", gap: "var(--space-4)", alignItems: "start" }}>

        {/* ── Colonna sinistra: documento originale ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", position: "sticky", top: "var(--space-4)" }}>
          <PdfPreview practiceId={invoice.id} filename={invoice.pdf_filename} pageCount={invoice.pdf_page_count ?? null} />
          <EmailPanel invoiceId={invoice.id} />
        </div>

        {/* ── Colonna destra: dati estratti con editing inline ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", minWidth: 0 }}>
          {/* Documento */}
          <div className="card">
            <div className="card-header-accent card-header-accent-brand">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-brand-primary)" }}>description</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-brand-primary)" }}>Documento</span>
            </div>
            <div className="form-group" style={{ marginBottom: "var(--space-2)" }}>
              <label className="form-label">Tipo documento</label>
              {readOnly ? (
                <div className="text-sm" style={{ padding: "var(--space-2) 0" }}>
                  {data?.documento?.tipo === "fattura" ? "Fattura" : data?.documento?.tipo === "credit_memo" ? "Credit memo" : "Sconosciuto"}
                </div>
              ) : (
                <select className="form-select" style={{ width: "100%", fontSize: "var(--font-size-sm)" }}
                  value={String(effectiveValue("documento.tipo") ?? "unknown")}
                  onChange={(e) => handleCorrect("documento.tipo", e.target.value)}>
                  {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
            </div>
            <EditableField label="Numero" value={effectiveValue("documento.numero")} originalValue={originalValue("documento.numero")} readOnly={readOnly}
              onChange={(v) => handleCorrect("documento.numero", v)} onReset={() => resetField("documento.numero")} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
              <EditableField label="Data emissione" value={effectiveValue("documento.data_emissione")} originalValue={originalValue("documento.data_emissione")} readOnly={readOnly} placeholder="YYYY-MM-DD"
                onChange={(v) => handleCorrect("documento.data_emissione", v)} onReset={() => resetField("documento.data_emissione")} />
              <EditableField label="Data scadenza" value={effectiveValue("documento.data_scadenza")} originalValue={originalValue("documento.data_scadenza")} readOnly={readOnly} placeholder="YYYY-MM-DD"
                onChange={(v) => handleCorrect("documento.data_scadenza", v)} onReset={() => resetField("documento.data_scadenza")} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
              <EditableField label="Termini pagamento" value={effectiveValue("documento.termini_pagamento")} originalValue={originalValue("documento.termini_pagamento")} readOnly={readOnly}
                onChange={(v) => handleCorrect("documento.termini_pagamento", v)} onReset={() => resetField("documento.termini_pagamento")} />
              <EditableField label="Incoterms" value={effectiveValue("documento.incoterms")} originalValue={originalValue("documento.incoterms")} readOnly={readOnly}
                onChange={(v) => handleCorrect("documento.incoterms", v)} onReset={() => resetField("documento.incoterms")} />
            </div>
          </div>

          {/* Fornitore */}
          <div className="card">
            <div className="card-header-accent card-header-accent-info">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-info)" }}>factory</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-info)" }}>Fornitore</span>
            </div>
            <EditableField label="Ragione sociale" value={effectiveValue("fornitore.ragione_sociale")} originalValue={originalValue("fornitore.ragione_sociale")} readOnly={readOnly}
              onChange={(v) => handleCorrect("fornitore.ragione_sociale", v)} onReset={() => resetField("fornitore.ragione_sociale")} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
              <EditableField label="Partita IVA" value={effectiveValue("fornitore.partita_iva")} originalValue={originalValue("fornitore.partita_iva")} readOnly={readOnly}
                onChange={(v) => handleCorrect("fornitore.partita_iva", v)} onReset={() => resetField("fornitore.partita_iva")} />
              <EditableField label="Paese" value={effectiveValue("fornitore.paese")} originalValue={originalValue("fornitore.paese")} readOnly={readOnly}
                onChange={(v) => handleCorrect("fornitore.paese", v)} onReset={() => resetField("fornitore.paese")} />
            </div>
            <EditableField label="Indirizzo" value={effectiveValue("fornitore.indirizzo")} originalValue={originalValue("fornitore.indirizzo")} readOnly={readOnly}
              onChange={(v) => handleCorrect("fornitore.indirizzo", v)} onReset={() => resetField("fornitore.indirizzo")} />
            {/* Codice Oracle: read-only (dal filename), con badge */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                Codice Oracle
                <span className="badge badge-neutral" title="Estratto dal nome del file (anchor)">
                  <span className="material-symbols-outlined" style={{ fontSize: 12 }}>attach_file</span> da filename
                </span>
              </label>
              <div className="text-sm" style={{
                padding: "var(--space-2) var(--space-3)", background: "var(--indaco-gray-50)",
                borderRadius: "var(--border-radius-sm)", border: "1px dashed var(--border-color)",
                fontFamily: "monospace",
              }}>
                {invoice.codice_fornitore_filename || data?.fornitore?.codice_oracle || "—"}
              </div>
            </div>
          </div>

          {/* Cliente */}
          <div className="card">
            <div className="card-header-accent card-header-accent-success">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-success)" }}>store</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-success)" }}>Cliente / Destinatario</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
              <EditableField label="Rag. soc. fatturazione" value={effectiveValue("cliente.ragione_sociale_fatturazione")} originalValue={originalValue("cliente.ragione_sociale_fatturazione")} readOnly={readOnly}
                onChange={(v) => handleCorrect("cliente.ragione_sociale_fatturazione", v)} onReset={() => resetField("cliente.ragione_sociale_fatturazione")} />
              <EditableField label="P.IVA fatturazione" value={effectiveValue("cliente.piva_fatturazione")} originalValue={originalValue("cliente.piva_fatturazione")} readOnly={readOnly}
                onChange={(v) => handleCorrect("cliente.piva_fatturazione", v)} onReset={() => resetField("cliente.piva_fatturazione")} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
              <EditableField label="Rag. soc. spedizione" value={effectiveValue("cliente.ragione_sociale_spedizione")} originalValue={originalValue("cliente.ragione_sociale_spedizione")} readOnly={readOnly}
                onChange={(v) => handleCorrect("cliente.ragione_sociale_spedizione", v)} onReset={() => resetField("cliente.ragione_sociale_spedizione")} />
              <EditableField label="Indirizzo spedizione" value={effectiveValue("cliente.indirizzo_spedizione")} originalValue={originalValue("cliente.indirizzo_spedizione")} readOnly={readOnly}
                onChange={(v) => handleCorrect("cliente.indirizzo_spedizione", v)} onReset={() => resetField("cliente.indirizzo_spedizione")} />
            </div>
            <EditableField label="Codice cliente interno" value={effectiveValue("cliente.codice_cliente_interno")} originalValue={originalValue("cliente.codice_cliente_interno")} readOnly={readOnly}
              onChange={(v) => handleCorrect("cliente.codice_cliente_interno", v)} onReset={() => resetField("cliente.codice_cliente_interno")} />
          </div>

          {/* Ordine / Logistica */}
          <div className="card">
            <div className="card-header-accent card-header-accent-neutral">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-gray-700)" }}>local_shipping</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-700)" }}>Ordine / Logistica</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
              <EditableField label="Numero PO" value={effectiveValue("ordine_logistica.numero_po")} originalValue={originalValue("ordine_logistica.numero_po")} readOnly={readOnly}
                onChange={(v) => handleCorrect("ordine_logistica.numero_po", v)} onReset={() => resetField("ordine_logistica.numero_po")} />
              <EditableField label="Tracking" value={effectiveValue("ordine_logistica.tracking")} originalValue={originalValue("ordine_logistica.tracking")} readOnly={readOnly}
                onChange={(v) => handleCorrect("ordine_logistica.tracking", v)} onReset={() => resetField("ordine_logistica.tracking")} />
            </div>
          </div>

          {/* Totali */}
          <div className="card">
            <div className="card-header-accent card-header-accent-brand">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-brand-primary)" }}>payments</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-brand-primary)" }}>Totali</span>
            </div>
            <EditableField label="Valuta" value={effectiveValue("totali.valuta")} originalValue={originalValue("totali.valuta")} readOnly={readOnly}
              onChange={(v) => handleCorrect("totali.valuta", v)} onReset={() => resetField("totali.valuta")} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
              <EditableField label="Imponibile" type="number" value={effectiveValue("totali.imponibile")} originalValue={originalValue("totali.imponibile")} readOnly={readOnly}
                onChange={(v) => handleCorrect("totali.imponibile", v)} onReset={() => resetField("totali.imponibile")} />
              <EditableField label="IVA / Tasse" type="number" value={effectiveValue("totali.iva_tasse")} originalValue={originalValue("totali.iva_tasse")} readOnly={readOnly}
                onChange={(v) => handleCorrect("totali.iva_tasse", v)} onReset={() => resetField("totali.iva_tasse")} />
            </div>
            {/* Totale: evidenziato se negativo */}
            {(() => {
              const totale = effectiveValue("totali.totale");
              const num = totale != null && totale !== "" ? Number(totale) : null;
              const isNegative = num != null && num < 0;
              return (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Totale</label>
                  {readOnly ? (
                    <div className="text-sm" style={{
                      fontSize: "var(--font-size-lg)", fontWeight: "var(--font-weight-bold)",
                      color: isNegative ? "var(--indaco-danger)" : "var(--indaco-gray-900)",
                    }}>
                      {num != null ? num.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                    </div>
                  ) : (
                    <input type="number" className="form-input"
                      style={{ width: "100%", fontSize: "var(--font-size-lg)", fontWeight: "var(--font-weight-bold)",
                               color: isNegative ? "var(--indaco-danger)" : "inherit",
                               borderColor: isNegative ? "var(--indaco-warning)" : undefined,
                               background: isNegative ? "var(--indaco-warning-light)" : undefined }}
                      value={totale == null ? "" : String(totale)}
                      onChange={(e) => handleCorrect("totali.totale", e.target.value)} />
                  )}
                  {isNegative && (
                    <div className="text-xs" style={{ color: "var(--indaco-warning)", marginTop: "var(--space-1)" }}>
                      Totale negativo (credit memo)
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Righe articoli — tabella compatta */}
          <div className="card">
            <div className="card-header-accent card-header-accent-neutral">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-gray-700)" }}>inventory_2</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-700)" }}>Righe articoli</span>
              <span className="text-muted text-sm" style={{ marginLeft: "auto" }}>{data?.righe?.length ?? 0} righe</span>
            </div>
            {data?.righe && data.righe.length > 0 ? (
              <div className="table-container">
                <table style={{ minWidth: "max-content" }}>
                  <thead>
                    <tr>
                      {["#", "Codice", "Descrizione", "Qtà", "UM", "Prezzo unit.", "Sconto", "Totale", ""].map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.righe.map((r, i) => {
                      const isModified = ["codice_articolo","descrizione","quantita","um","prezzo_unitario","sconto","totale_riga"].some(
                        (f) => corrections[`righe.${i}.${f}`] !== undefined
                      );
                      const resetRow = () => {
                        ["codice_articolo","descrizione","quantita","um","prezzo_unitario","sconto","totale_riga"].forEach(
                          (f) => resetField(`righe.${i}.${f}`)
                        );
                      };
                      const cellInput = (field: string, numeric = false, width = 90) => {
                        const orig = originalValue(`righe.${i}.${field}`);
                        const val = effectiveValue(`righe.${i}.${field}`);
                        const changed = corrections[`righe.${i}.${field}`] !== undefined;
                        return (
                          <div style={{ position: "relative" }}>
                            <input
                              type={numeric ? "number" : "text"}
                              className="form-input"
                              disabled={readOnly}
                              value={val ?? ""}
                              onChange={(e) => handleCorrect(`righe.${i}.${field}`, e.target.value)}
                              style={{ width, background: changed ? "var(--indaco-warning-light)" : undefined }}
                              title={changed ? `Originale: ${orig ?? "—"}` : undefined}
                            />
                            {changed && !readOnly && (
                              <button
                                className="icon-btn"
                                onClick={() => resetField(`righe.${i}.${field}`)}
                                title={`Ripristina: ${orig ?? "—"}`}
                                style={{ position: "absolute", top: 2, right: 2 }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>undo</span>
                              </button>
                            )}
                          </div>
                        );
                      };
                      return (
                        <tr key={i} style={{ background: isModified ? "var(--indaco-warning-light)" : undefined }}>
                          <td style={{ whiteSpace: "nowrap", color: "var(--indaco-gray-500)", fontSize: "var(--font-size-xs)" }}>
                            {i + 1}
                            {r.ean_barcode && <div style={{ fontFamily: "monospace", fontSize: 10, color: "var(--indaco-gray-400)" }}>{r.ean_barcode}</div>}
                          </td>
                          <td>{cellInput("codice_articolo", false, 110)}</td>
                          <td style={{ minWidth: 200 }}>{cellInput("descrizione", false, "100%" as any)}</td>
                          <td>{cellInput("quantita", true, 70)}</td>
                          <td>{cellInput("um", false, 50)}</td>
                          <td>{cellInput("prezzo_unitario", true, 90)}</td>
                          <td>{cellInput("sconto", false, 72)}</td>
                          <td>{cellInput("totale_riga", true, 90)}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {isModified && !readOnly && (
                              <button className="icon-btn" onClick={resetRow} title="Annulla modifiche riga">
                                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>undo</span>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: "var(--space-4)", textAlign: "center", color: "var(--indaco-gray-500)" }} className="text-sm">
                <span className="material-symbols-outlined" style={{ fontSize: 24 }}>inventory_2</span>
                <div>Nessuna riga articolo estratta.</div>
              </div>
            )}
          </div>

          {/* Testo OCR (debug, collapsible) */}
          {invoice.ocr_text && (
            <details>
              <summary style={{ cursor: "pointer", fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-500)", padding: "var(--space-2)" }}>
                Testo OCR (debug)
              </summary>
              <div className="card" style={{ marginTop: "var(--space-2)", padding: "var(--space-3)" }}>
                <pre style={{
                  maxHeight: 400, overflow: "auto", whiteSpace: "pre-wrap",
                  fontSize: "var(--font-size-xs)", lineHeight: 1.5, color: "var(--indaco-gray-800)",
                  fontFamily: "monospace",
                }}>
                  {invoice.ocr_text}
                </pre>
              </div>
            </details>
          )}
        </div>
      </div>
        </div>

      {/* ── Modal Approva ── */}
      {showApproveModal && (
        <div className="dialog-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Conferma approvazione</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowApproveModal(false)}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
              </button>
            </div>
            <div className="text-sm">
              {correctionsCount > 0 ? (
                <div style={{ marginBottom: "var(--space-3)" }}>
                  <div className="fw-semibold" style={{ marginBottom: "var(--space-1)" }}>
                    Verranno applicate {correctionsCount} correzion{correctionsCount === 1 ? "e" : "i"}:
                  </div>
                  <div style={{ maxHeight: 200, overflowY: "auto", background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-sm)", padding: "var(--space-2)" }}>
                    {Object.entries(effectiveCorrections).map(([k, v]) => (
                      <div key={k} style={{ fontSize: "var(--font-size-xs)", fontFamily: "monospace", marginBottom: 2 }}>
                        <strong>{k}</strong>: {String(v)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-muted" style={{ marginBottom: "var(--space-3)" }}>
                  Nessuna correzione applicata — verranno confermati i dati estratti così come sono.
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Nota approvazione (opzionale)</label>
                <textarea className="form-textarea" rows={2} value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  placeholder="Eventuali note per l'approvazione..." disabled={isProcessing} />
              </div>
            </div>
            <div className="dialog-footer">
              <button className="btn btn-outline" onClick={() => setShowApproveModal(false)} disabled={isProcessing}>Annulla</button>
              <button className="btn btn-primary" onClick={handleApprove} disabled={isProcessing}>
                {isProcessing ? <span className="spinner" /> : <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>}
                Conferma approvazione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Rifiuta ── */}
      {showRejectModal && (
        <div className="dialog-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Rifiuta fattura</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowRejectModal(false)}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
              </button>
            </div>
            <div className="form-group">
              <label className="form-label">Motivo rifiuto <span style={{ color: "var(--indaco-danger)" }}>*</span> (obbligatorio)</label>
              <textarea className="form-textarea" rows={3} value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Specifica il motivo del rifiuto..." disabled={isProcessing} />
            </div>
            <div className="dialog-footer">
              <button className="btn btn-outline" onClick={() => setShowRejectModal(false)} disabled={isProcessing}>Annulla</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={isProcessing || !rejectNote.trim()}>
                {isProcessing ? <span className="spinner" /> : null}
                Conferma rifiuto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Riprocessa ── */}
      {showReprocessModal && (
        <div className="dialog-overlay" onClick={() => setShowReprocessModal(false)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Riprocessa fattura</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowReprocessModal(false)}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
              </button>
            </div>
            <div className="text-sm" style={{ marginBottom: "var(--space-3)" }}>
              Verrà rieseguito OCR + grafo di estrazione sul PDF originale. I dati estratti correnti e le eventuali correzioni non salvate verranno <strong>sostituiti</strong>. Procedere?
            </div>
            <div className="dialog-footer">
              <button className="btn btn-outline" onClick={() => setShowReprocessModal(false)} disabled={isProcessing}>Annulla</button>
              <button className="btn btn-primary" onClick={handleReprocess} disabled={isProcessing}>
                {isProcessing ? <span className="spinner" /> : <span className="material-symbols-outlined" style={{ fontSize: 18 }}>autorenew</span>}
                Riprocessa
              </button>
            </div>
          </div>
        </div>
      )}
    </DetailPageShell>
    </>
  );
}
